import { Button, Input, Space, Tag, Typography, Pagination } from "antd";
import { Link, useLocation } from "wouter";
import { useEffect, useMemo, useState, useCallback } from "react";
import { CopyFilled, EditOutlined, EyeFilled } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { OrderRequestsList } from "../../../redux/actions/adminActions/listsAction";
import Alertify from "../../../services/alertify";
import CommonAdminTable from "../../components/commonTable";
import { getQueryParams } from "../../../utilities/getQueryParams";
import { OrderDetailsAPI } from "../../../redux/actions/adminActions/orderDetailsAction";
import { SaveDraft } from "../../../redux/actions/adminActions/newOrderRequestAction";
import { ADMIN } from "../../../redux/constants/roles";
import { LoaderIcon } from "react-hot-toast";

const { Title } = Typography;

const DealerOrderRequestList = () => {
  const [searchValue, setSearchValue] = useState("");
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);

  const authData = useSelector((state) => state.auth_store);

  const dispatch = useDispatch();

  useEffect(() => {
    document.title = "Order Requests: ALP Shop";
  }, []);
  const [, navigate] = useLocation();
  const params = getQueryParams();
  const initialPage = params.page || 1;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [isAdmin, setIsAdmin] = useState(false);
  const handlePageChange = (page) => {
    setCurrentPage(page);
    navigate(`/admin/dealer-orders?page=${page}`);
  };
  const fetchOrderRequests = useCallback(() => {
    const data = {
      search: searchValue,
      page: currentPage,
      orderBy: "dealer",
    };
    setLoading(true);

    dispatch(OrderRequestsList(data))
      .then((response) => {
        // console.log("List is-----:", response);
        const total = response?.payload?.data?.pagination?.total || 0;
        setTotalOrders(total);
        setLoading(false);

        // console.log("Total orders", total);
      })
      .catch((error) => {
        Alertify.error("Failed to fetch Order Request List: " + error.message);
        setLoading(false);
      });
  }, [dispatch, searchValue, currentPage]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchOrderRequests();
    }, 300); // Delay of 300 milliseconds for debouncing

    return () => {
      clearTimeout(handler);
    };
  }, [fetchOrderRequests]);

  const orderRequests = useSelector((state) => state.all_lists);

  const all_order_requests = useMemo(() => {
    return orderRequests.all_order_request_list || [];
  }, [orderRequests]);
  const handleApprove = (orderId) => {
    console.log("Approve order:", orderId);
    const data = {
      uuid: `${orderId}`,
    };
    if (orderId) {
      dispatch(OrderDetailsAPI(data))
        .then((response) => {
          const formData = response.payload.data.data;
          const newFormData = {
            ...formData,
            requestId: orderId,
          };
          dispatch(SaveDraft({ details: newFormData }))
            .then((response) => {
              const resUuid = response?.payload?.data?.data?.uuid;
              if (!params.UUID) {
                const newUrl = `/admin/order-requests/new-order?UUID=${resUuid}`;
                window.history.replaceState(null, "", newUrl);
              }
              Alertify.success("Order draft saved successfully!");
            })
            .catch((error) => {
              Alertify.error("Failed to save draft: " + error.message);
            });
        })
        .catch((error) => {
          Alertify.error("Failed to fetch Order Details: " + error.message);
        });
    }
  };
  const columns = [
    {
      title: "Index",
      dataIndex: "index",
      key: "index",
      align: "center",
      render: (text, record, index) => (currentPage - 1) * 10 + index + 1,
    },
    {
      title: "Request Id",
      dataIndex: "primaryId",
      key: "primaryId",
      fixed: "left",
      render: (text) => <Link to="#">{text}</Link>,
    },
    {
      title: "User",
      dataIndex: "userName",
      key: "userName",
    },
    {
      title: "Order Total",
      dataIndex: "orderTotal",
      key: "orderTotal",
    },
    {
      title: "Order Type",
      dataIndex: "orderType",
      key: "orderType",
    },
    {
      title: "Requested Date",
      dataIndex: "requestedDate",
      key: "requestedDate",
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "status",
      render: (status) => {
        let color = status === "approved" ? "green" : "geekblue";
        return (
          <Tag color={color} key={status}>
            {status.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Placed At",
      dataIndex: "placedAt",
      key: "placedAt",
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: "200px",
      render: (record) => (
        <Space size="small">
          {record.status === "pending" && !isAdmin && (
            <Button
              className="rounded-md bg-transparent border-0 shadow-none"
              onClick={() => handleApprove(record.primaryId)}
            >
              <EditOutlined />
              Edit
            </Button>
          )}
          <Link
            to={`/admin/order-requests/order-detail?orderId=${record.primaryId}`}
            className="text-red-600"
          >
            <EyeFilled className="me-1" />
            View
          </Link>

          {record.status !== "pending" && !isAdmin && (
            <Link
              to={`/admin/order-requests/new-order?orderId=${record.orderId}`}
              className="text-blue-700"
            >
              <CopyFilled className="me-1" />
              Clone Order
            </Link>
          )}
        </Space>
      ),
    },
  ];

  // Add a unique key to each order request
  const dataSource = all_order_requests.map((order, index) => ({
    ...order,
    key: order.orderId || index, // Use orderId or index as a fallback key
  }));

  useEffect(() => {
    if (authData.role === ADMIN) {
      setIsAdmin(true);
    }
  }, [setIsAdmin, authData.role]);
  return (
    <>
      <Title level={2} className="text-left">
        Dealer Order Requests
      </Title>
      <div className="mb-8 flex items-center justify-between max-sm:flex-col max-sm:gap-4">
        <div className="sm:w-1/3">
          <Input
            placeholder="Search"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        {!isAdmin && (
          <Link to="/admin/order-requests/new-order">
            <Button type="primary" className="rounded-md">
              NEW ORDER REQUEST +
            </Button>
          </Link>
        )}
      </div>
      {loading ? (
        <div className="flex items-center justify-center pt-60">
          <LoaderIcon className="w-[3vw] h-[3vw] " />
        </div>
      ) : (
        <>
          <CommonAdminTable columns={columns} data={dataSource} />
          <div className="flex justify-between items-center mt-4">
            <Pagination
              current={currentPage}
              total={totalOrders} // Use totalOrders state
              pageSize={10}
              onChange={handlePageChange}
              showSizeChanger={false}
            />
          </div>
        </>
      )}
    </>
  );
};

export default DealerOrderRequestList;
